import React, { useCallback } from 'react';
import { Chip, TextField, Select, MenuItem, Button } from '@mui/material';
import PropTypes from 'prop-types';
import "../../assets/scss/components/filters/DocumentFilters.scss";

export const NON_EXPIRABLE_DOCUMENTS = [
  "Foto de Perfil",
  "Documento de identidad",
  "Certificados Laborales"
];

export default function DocumentFilters({ 
  documents,
  onSearchChange,
  onFilterChange,
  searchValue,
  filterValue,
  onExpirationFilterClick,
  handleOpenMultiDownload,
  theme,
  userName = "Usuario"
}) {
  const getExpirationStatus = () => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);
    
    const filterExpirableDocuments = (doc) => {
      // Si el documento está en la lista de no expirables, lo excluimos
      if (NON_EXPIRABLE_DOCUMENTS.includes(doc.document_type?.name)) {
        return false;
      }
      return doc.expiration_date;
    };
  
    const expired = documents.filter(doc => 
      filterExpirableDocuments(doc) && new Date(doc.expiration_date) < new Date()
    ).length;
    
    const aboutToExpire = documents.filter(doc => {
      if (!filterExpirableDocuments(doc)) return false;
      
      const expirationDate = new Date(doc.expiration_date);
      return expirationDate > new Date() && expirationDate <= thirtyDaysFromNow;
    }).length;
  
    return { expired, aboutToExpire };
  };

  // Obtener estadísticas de documentos por categoría
  const getDocumentStats = () => {
    const personalDocTypes = ["Foto de Perfil", "Documento de identidad", "Licencia de Conducción"];
    const certificateTypes = [
      "Curso de Sustancias Peligrosas",
      "Curso de Seguridad Vial",
      "Curso de Manejo de Extintores y Contraincendios",
      "Curso Mecánica Básica",
      "Curso Primeros Auxilios",
      "Curso de Manejo Defensivo",
      "Curso Trabajo En Alturas",
      "MAE",
      "Módulos Driving",
    ];

    const personal = documents.filter(doc => 
      personalDocTypes.includes(doc.document_type?.name)
    ).length;

    const certificates = documents.filter(doc => 
      certificateTypes.includes(doc.document_type?.name)
    ).length;

    const required = documents.filter(doc => doc.required).length;

    return { personal, certificates, required, total: documents.length };
  };

  const { expired, aboutToExpire } = getExpirationStatus();
  const documentStats = getDocumentStats();
  const shouldShowExpirationStatus = expired > 0 || aboutToExpire > 0;
  const shouldShowStats = documentStats.total > 0;

  const handleMultiDownloadClick = useCallback(() => {
    handleOpenMultiDownload();
  }, [handleOpenMultiDownload]);

  return (
    <div className={`GM__${theme}-user-document-filters-container`}>
      <div className={`GM__${theme}-filters-section`}>
        {/* Sección de estado y estadísticas */}
        <div className={`GM__${theme}-status-section`}>
          {/* Estado de expiración */}
          {shouldShowExpirationStatus && (
            <Button 
              className={`GM__${theme}-expiration-status-btn`}
              onClick={() => onExpirationFilterClick('expiration')}
              variant="text"
            >
              <div className={`GM__${theme}-expiration-status`}>
                {expired > 0 && (
                  <span className={`GM__${theme}-status-item`}>
                    Documentos expirados 
                    <Chip 
                      label={expired} 
                      size="small" 
                      className={`GM__${theme}-status-chip GM__${theme}-expired`}
                    />
                  </span>
                )}
                {aboutToExpire > 0 && (
                  <span className={`GM__${theme}-status-item`}>
                    A punto de expirar 
                    <Chip 
                      label={aboutToExpire} 
                      size="small" 
                      className={`GM__${theme}-status-chip GM__${theme}-about-to-expire`}
                    />
                  </span>
                )}
              </div>
            </Button>
          )}

          {/* Estadísticas de documentos */}
          {shouldShowStats && (
            <div className={`GM__${theme}-document-stats`}>
              <div className={`GM__${theme}-stats-container`}>
                <div className={`GM__${theme}-stat-item`}>
                  <span className={`GM__${theme}-stat-label`}>Total:</span>
                  <Chip 
                    label={documentStats.total} 
                    size="small" 
                    className={`GM__${theme}-stat-chip GM__${theme}-total`}
                  />
                </div>
                
                {documentStats.personal > 0 && (
                  <div className={`GM__${theme}-stat-item`}>
                    <span className={`GM__${theme}-stat-label`}>Personales:</span>
                    <Chip 
                      label={documentStats.personal} 
                      size="small" 
                      className={`GM__${theme}-stat-chip GM__${theme}-personal`}
                    />
                  </div>
                )}
                
                {documentStats.certificates > 0 && (
                  <div className={`GM__${theme}-stat-item`}>
                    <span className={`GM__${theme}-stat-label`}>Certificados:</span>
                    <Chip 
                      label={documentStats.certificates} 
                      size="small" 
                      className={`GM__${theme}-stat-chip GM__${theme}-certificates`}
                    />
                  </div>
                )}
                
                {documentStats.required > 0 && (
                  <div className={`GM__${theme}-stat-item`}>
                    <span className={`GM__${theme}-stat-label`}>Requeridos:</span>
                    <Chip 
                      label={documentStats.required} 
                      size="small" 
                      className={`GM__${theme}-stat-chip GM__${theme}-required`}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Sección de controles */}
        <div className={`GM__${theme}-controls-section`}>
          <div className={`GM__${theme}-filter-controls`}>
            <TextField
              size="small"
              placeholder="Buscar documentos..."
              value={searchValue}
              onChange={onSearchChange}
              className={`GM__${theme}-search-field`}
            />
            
            <Select
              size="small"
              value={filterValue}
              onChange={onFilterChange}
              className={`GM__${theme}-filter-select`}
            >
              <MenuItem value="all">Todos los documentos</MenuItem>
              <MenuItem value="personal">Documentos personales</MenuItem>
              <MenuItem value="empty">Documentos pendientes</MenuItem>
              <MenuItem value="required">Documentos requeridos</MenuItem>
              <MenuItem value="not-required">Documentos no requeridos</MenuItem>
              <MenuItem value="expiration">Documentos por vencer</MenuItem>
            </Select>
          </div>
          
          <div className={`GM__${theme}-download-section`}>
            <button 
              className={`GM__${theme}-multi-download-btn`} 
              onClick={handleMultiDownloadClick}
              title={`Descargar documentos de ${userName}`}
            >
              <span className={`GM__${theme}-download-text`}>
                Descargar múltiples
              </span>
              <small className={`GM__${theme}-download-subtitle`}>
                Documentos de {userName}
              </small>
            </button>
          </div>
        </div>
      </div>

      {/* Información contextual del filtro activo */}
      {filterValue !== 'all' && (
        <div className={`GM__${theme}-filter-context`}>
          <div className={`GM__${theme}-context-info`}>
            <span className={`GM__${theme}-context-label`}>
              Filtro activo:
            </span>
            <span className={`GM__${theme}-context-value`}>
              {getFilterDisplayName(filterValue)}
            </span>
            <button
              className={`GM__${theme}-clear-filter-btn`}
              onClick={() => onFilterChange({ target: { value: 'all' } })}
              title="Limpiar filtro"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // Función auxiliar para obtener el nombre del filtro
  function getFilterDisplayName(filter) {
    const filterNames = {
      personal: 'Documentos personales',
      empty: 'Documentos pendientes',
      required: 'Documentos requeridos',
      'not-required': 'Documentos no requeridos',
      expiration: 'Documentos por vencer'
    };
    return filterNames[filter] || filter;
  }
}

DocumentFilters.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      expiration_date: PropTypes.string,
      document_type: PropTypes.shape({
        name: PropTypes.string
      }),
      required: PropTypes.bool
    })
  ).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  filterValue: PropTypes.oneOf([
    'all',
    'personal',
    'empty',
    'required',
    'not-required',
    'expiration'
  ]).isRequired,
  onExpirationFilterClick: PropTypes.func.isRequired,
  handleOpenMultiDownload: PropTypes.func.isRequired,
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  userName: PropTypes.string
};